import React, { useState, useEffect, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Skeleton from '@mui/material/Skeleton';
import Slide from '@mui/material/Slide';
import statiRiconosciuti from '../helpers/statiRiconosciuti';
import AttentionBlock from '../utility/AttentionBlock';
import { GridView } from '../GridView';
import { SuggestionBar } from './SuggestionBar';
import { getSessionIdToken } from '../auth/apiAuth';

import axios from 'axios';

// ─── GhostSkeleton ───────────────────────────────────────────────────────────

function GhostPageRows({ count }) {
  return Array.from({ length: count }).map((_, i) => (
    <Box key={i} sx={{ display: 'flex', gap: 1, px: 1, py: 0.4, alignItems: 'center' }}>
      <Skeleton variant="circular" animation="wave" width={8} height={8} sx={{ flexShrink: 0 }} />
      <Skeleton variant="text" animation="wave" height={13} sx={{ flex: 1 }} />
      <Skeleton variant="rectangular" animation="wave" width={46} height={16} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
      <Skeleton variant="rectangular" animation="wave" width={32} height={16} sx={{ borderRadius: 0.5, flexShrink: 0 }} />
    </Box>
  ));
}

function GhostSkeleton() {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
      {/* Tab bar ghost */}
      <Box sx={{ display: 'flex', gap: 2, px: 3, py: 0.8, bgcolor: 'rgba(0,0,0,0.02)', borderBottom: '1px solid #e0e0e0' }}>
        <Skeleton variant="text" animation="wave" width={55} height={20} />
        <Skeleton variant="text" animation="wave" width={65} height={20} />
        <Skeleton variant="text" animation="wave" width={70} height={20} />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Filmstrip */}
        <Box sx={{ width: 72, flexShrink: 0, borderRight: '1px solid #e0e0e0', p: 0.8, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.8, alignItems: 'center', overflow: 'hidden' }}>
          {Array.from({ length: 7 }).map((_, i) => (
            <Box key={i} sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3,
              border: i === 0 ? '2px solid' : '1px solid transparent',
              borderColor: i === 0 ? 'primary.light' : 'transparent',
              borderRadius: 0.5, p: 0.3,
            }}>
              <Skeleton variant="rectangular" animation="wave" width={48} height={68} sx={{ borderRadius: 0.3 }} />
              <Skeleton variant="text" animation="wave" width={14} height={11} />
            </Box>
          ))}
        </Box>

        {/* Preview page */}
        <Box sx={{
          width: 'calc((100vh - 120px) * 0.7)',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          p: 2, pt: 1.5,
          background: '#eeeeee',
          borderRight: '1px solid #e0e0e0',
        }}>
          <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton variant="text" animation="wave" width={80} height={14} sx={{ alignSelf: 'center' }} />
            <Box sx={{
              width: '100%',
              aspectRatio: '175/250',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridTemplateRows: 'repeat(3, 1fr)',
              gap: '5px',
              p: 1.5,
              bgcolor: '#fff',
              borderRadius: 0.5,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}>
              {Array.from({ length: 9 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" animation="wave" sx={{ width: '100%', height: '100%', borderRadius: 0.3 }} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* CSV list */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Sticky header */}
          <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
            <Skeleton variant="text" animation="wave" width={20} height={10} sx={{ flexShrink: 0 }} />
            <Skeleton variant="text" animation="wave" width={60} height={10} sx={{ flexShrink: 0 }} />
            <Skeleton variant="text" animation="wave" height={10} sx={{ flex: 1 }} />
            <Skeleton variant="text" animation="wave" width={40} height={10} sx={{ flexShrink: 0 }} />
            <Skeleton variant="text" animation="wave" width={35} height={10} sx={{ flexShrink: 0 }} />
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', px: 0.5 }}>
            {/* Page 2 */}
            <Skeleton variant="rectangular" animation="wave" height={26} sx={{ borderRadius: 0.3, my: 0.3 }} />
            <GhostPageRows count={4} />
            {/* Page 3 */}
            <Skeleton variant="rectangular" animation="wave" height={26} sx={{ borderRadius: 0.3, my: 0.3 }} />
            <GhostPageRows count={3} />
            {/* Page 4 */}
            <Skeleton variant="rectangular" animation="wave" height={26} sx={{ borderRadius: 0.3, my: 0.3 }} />
            <GhostPageRows count={3} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}


const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * GridViewDialog — Dialog fullscreen che:
 * 1. Fa polling sullo status DI fino a "data integration complete"
 * 2. Chiama GET /gridview-data per ottenere pre-signed URL
 * 3. Fetcha CSV e config, monta GridView con i dati
 * 4. Al click "Launch" dal GridView, uploada CSV e lancia la paginazione
 *
 * Props:
 *   open          {boolean}  — visibilità del dialog
 *   reqId         {string}   — reqId della richiesta DI
 *   projectConf   {object}   — conf del progetto (da fetchConf)
 *   projectData   {object}   — dati progetto (bucket, key, dataFolder...)
 *   auth          {object}   — auth props
 *   onClose       {function} — callback chiusura
 *   onLaunched    {function} — callback dopo secondo lancio
 */
export default function GridViewDialog({
  open, reqId, projectConf, projectData, auth, onClose, onLaunched
}) {
  // Stati: 'polling' | 'loading-data' | 'ready' | 'error' | 'launching'
  const [phase, setPhase] = useState('polling');
  const [status, setStatus] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [errors, setErrors] = useState([]);
  const [csvData, setCsvData] = useState(null);
  const [csvFileName, setCsvFileName] = useState(null);
  const [layoutConfig, setLayoutConfig] = useState(null);
  const [splitFiles, setSplitFiles] = useState([]);
  const [confirmExit, setConfirmExit] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const [gridInfo, setGridInfo] = useState(null);

  const pollingRef = useRef(null);
  const gridViewRef = useRef(null);

  // ── Cover con prodotti ("Highlights") ──────────────────────────────
  const hasCoverProducts = projectConf?.selectedPaginationOptions
    ?.some(opt => opt.nome === 'Cover Layout' && opt.value === 'Highlights')
    ?? false

  // ── Suggestion Bar ─────────────────────────────────────────────────
  const optimizations = layoutConfig?.meta?.optimizations;
  const availableAdvisors = [];
  if (optimizations?.imageSize?.enabled) availableAdvisors.push('imageFit');
  if (optimizations?.fillGaps?.enabled)  availableAdvisors.push('spaceFill');

  const activeAdvisors = new Set(gridInfo?.activeAdvisors || []);

  const handleToggleAdvisor = (advisor) => {
    const next = new Set(activeAdvisors);
    if (next.has(advisor)) next.delete(advisor);
    else next.add(advisor);
    gridViewRef.current?.setActiveAdvisors(next);
  };

  const handleApplyAll = (advisor) => {
    gridViewRef.current?.applyAll(advisor);
  };

  const handleUndoGlobal = (id) => {
    gridViewRef.current?.executeUndo(id);
  };

  // ── Polling status DI ──────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const token = await getSessionIdToken();
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/operations`, {
        headers: {
          'user_id': 'web-app',
          "content-type": "application/json",
          "Authorization": token,
          "is_admin": auth.userState.isAdmin,
          "reqid": reqId,
          "project_id": projectConf.project_id,
          "project_ids": projectConf.project_id,
          "stati": "tutti",
        }
      });

      const item = resp.data;
      if (item && item.status) {
        setStatus(item.status);
        const normalize = (arr) => (Array.isArray(arr) ? arr : [arr]).map(e =>
          typeof e === 'string' ? { name: e, header: [], data: [] } : e
        );
        if (item.warnings) setWarnings(normalize(item.warnings));
        if (item.errors) setErrors(normalize(item.errors));
      }
    } catch (err) {
      console.error('Polling status error');
    }
  }, [reqId, projectConf?.project_id, auth]);

  useEffect(() => {
    if (!open || !reqId) return;
    // Reset
    setPhase('polling');
    setStatus(null);
    setWarnings([]);
    setErrors([]);
    setCsvData(null);
    setLayoutConfig(null);
    setSplitFiles([]);
    setLaunchError(null);

    // Primo fetch immediato
    fetchStatus();
    pollingRef.current = setInterval(fetchStatus, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [open, reqId]);

  // ── Reagisci al cambio status ──────────────────────────────────────
  useEffect(() => {
    if (!status) return;

    if (status === 'validation failed') {
      if (pollingRef.current) clearInterval(pollingRef.current);
      setPhase('error');
      return;
    }

    if (status === 'data integration complete') {
      if (pollingRef.current) clearInterval(pollingRef.current);
      setPhase('loading-data');
      fetchGridViewData();
      return;
    }

    // Se lo status è terminale ma non è quello che aspettiamo
    if (statiRiconosciuti.terminati.has(status) && status !== 'data integration complete') {
      if (pollingRef.current) clearInterval(pollingRef.current);
      setPhase('error');
      setErrors([{ name: `Unexpected terminal status: ${status}`, header: [], data: [] }]);
    }
  }, [status]);

  // ── Fetch dati GridView (CSV split + config) ──────────────────────
  const fetchGridViewData = async () => {
    try {
      const token = await getSessionIdToken();
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/gridview-data`, {
        headers: {
          "Authorization": token,
          "reqid": reqId,
          "project_id": projectConf.project_id,
        }
      });

      const { splitFiles: files, configUrl } = resp.data;
      setSplitFiles(files || []);

      // Fetch config layout JSON
      let config = null;
      if (configUrl) {
        try {
          const configResp = await axios.get(configUrl);
          config = configResp.data;
        } catch (err) {
          console.warn('Config layout not found, GridView will use default');
        }
      }
      setLayoutConfig(config);

      // Fetch e merge CSV split files
      if (files && files.length > 0) {
        const csvTexts = await Promise.all(
          files.map(async (f) => {
            const csvResp = await axios.get(f.url, { responseType: 'text' });
            return csvResp.data;
          })
        );

        // Merge: prendi header dal primo file, poi tutte le righe
        function skipHeader(text) {
          const norm = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          let pos = 0, depth = 0;
          while (pos < norm.length) {
            if (norm[pos] === '"') depth = 1 - depth;
            if (norm[pos] === '\n' && depth === 0) break;
            pos++;
          }
          return norm.slice(pos + 1);
        }

        let mergedCsv = '';
        csvTexts.forEach((text, i) => {
          if (i === 0) {
            mergedCsv = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          } else {
            const body = skipHeader(text);
            if (body) mergedCsv += '\n' + body;
          }
        });

        setCsvData(mergedCsv);
        setCsvFileName(files.length === 1 ? files[0].name : `${files.length} files merged`);
        setPhase('rendering');
      } else {
        setPhase('error');
        setErrors([{ name: 'No split files found for this request', header: [], data: [] }]);
      }
    } catch (err) {
      console.error('Error fetching gridview data');
      setPhase('error');
      setErrors([{ name: `Error loading data: ${err.message}`, header: [], data: [] }]);
    }
  };

  // ── Secondo lancio: upload CSV modificato + triggerPagination ──────
  const handleGridViewLaunch = async (serializedCsv) => {
    setPhase('launching');
    try {
      const token = await getSessionIdToken();

      // 1. Fetch URL fresche e upload CSV modificato
      if (serializedCsv) {
        const freshResp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/gridview-data`, {
          headers: {
            "Authorization": token,
            "reqid": reqId,
            "project_id": projectConf.project_id,
          }
        });
        const freshFiles = freshResp.data.splitFiles || [];
        if (freshFiles.length > 0) {
          const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
          const csvBlob = new Blob([bom, serializedCsv], { type: 'text/csv; charset=utf-8' });
          const headerLine = serializedCsv.split('\n')[0];
          const emptyBlob = new Blob([bom, headerLine], { type: 'text/csv; charset=utf-8' });

          await Promise.all(freshFiles.map((f, i) =>
            axios.put(f.uploadUrl, i === 0 ? csvBlob : emptyBlob, {
              headers: { 'Content-Type': 'text/csv' }
            })
          ));
        }
      }

      // 2. Seconda triggerPagination con gridViewRelaunch
      const pagOptionsToSend = !projectConf.selectedPaginationOptions ? [] : projectConf.selectedPaginationOptions.map(opt => ({
        ...opt,
        value: opt.value !== undefined ? opt.value : opt.default
      }));

      // Aggiungi copertine, asset e dati aggiuntivi (stesso pattern di triggerPagination in Launch.js)
      const staticPagesList = !projectConf.selectedCovers ? [] : projectConf.selectedCovers.map(cover => ({
        nome: cover.folder, value: cover.tagsValues, filepath: cover.value,
      }));
      const assetsList = !projectConf.selectedAssets ? [] : projectConf.selectedAssets.map(asset => ({
        nome: asset.displayName ? asset.displayName : asset.folder, value: asset.value, filepath: asset.fullPath.replace('###', '/'),
      }));
      const additionalDataList = !projectConf.selectedAdditionalData ? [] : projectConf.selectedAdditionalData.map(data => ({
        nome: data.folder, value: data.value, filepath: data.fullPath.replace('###', '/'),
      }));
      pagOptionsToSend.push(...staticPagesList);
      pagOptionsToSend.push(...assetsList);
      pagOptionsToSend.push(...additionalDataList);

      // Aggiungi flag gridViewRelaunch
      pagOptionsToSend.push({ nome: 'gridViewRelaunch', value: true, hidden: true });

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/projects-webapp`,
        {
          "project_id": projectConf.project_id,
          "project_name": projectConf.project_name,
          "reqId": reqId,
          "email": auth.userState.email,
          "user_id": auth.user,
          "customer": projectConf.customer,
          "bucket": projectConf.bucket,
          "prefix": projectConf.project_id,
          "dataFolder": projectData.dataFolder,
          "key": encodeURIComponent(projectData.key),
          "projectConf": projectConf,
          "paginationOptions": pagOptionsToSend,
          "internalTest": projectConf.internalTest,
          "doOnlyValidation": false,
        },
        {
          headers: {
            'user_id': auth.userState.user,
            "content-type": "application/json",
            "Authorization": token,
          }
        }
      );

      onLaunched?.();
    } catch (err) {
      console.error('Error in GridView launch');
      setLaunchError(err.message);
      setPhase('ready'); // torna alla GridView per riprovare
    }
  };

  // ── Cancella la richiesta DI ────────────────────────────────────────
  const deleteRequest = async () => {
    try {
      const token = await getSessionIdToken();
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/operations`, {
        headers: {
          "Authorization": token,
          reqId,
          project_id: projectConf.project_id,
          bucket: projectConf.bucket,
          user_id: 'web-app'
        }
      });
      //console.log('GridView exit: request deleted', reqId);
    } catch (err) {
      console.error('Error deleting request on exit');
    }
  };

  // ── Exit con conferma ──────────────────────────────────────────────
  const handleExitClick = () => {
    if (phase === 'ready') {
      setConfirmExit(true);
    } else {
      onClose?.();
    }
  };

  const handleConfirmExit = async () => {
    setConfirmExit(false);
    await deleteRequest();
    onClose?.();
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={handleExitClick}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
          <Toolbar>
            <Typography sx={{ flex: 1 }} variant="h6" component="div">
              Layout Preview{projectData?.key ? ` — ${decodeURIComponent(projectData.key.split('/').pop())}` : csvFileName ? ` — ${csvFileName}` : ''}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Warning/Error badges */}
              {warnings.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', '& > *': { mb: '0 !important' } }}>
                  <AttentionBlock attentions={warnings} color='warning' title='Validation warnings' type='iconButton' size='medium' />
                </Box>
              )}
              {errors.length > 0 && phase !== 'error' && (
                <Box sx={{ display: 'flex', alignItems: 'center', '& > *': { mb: '0 !important' } }}>
                  <AttentionBlock attentions={errors} color='error' title='Validation errors' type='iconButton' size='medium' />
                </Box>
              )}

              {/* Status indicator */}
              {phase === 'polling' && status && (
                <Typography variant="body2" color="text.secondary">
                  {status}
                </Typography>
              )}

              {/* Exit + Launch buttons */}
              <Button
                variant="outlined"
                onClick={handleExitClick}
                sx={{ minWidth: 90, color: '#757575', borderColor: '#bdbdbd', '&:hover': { borderColor: '#9e9e9e', bgcolor: 'rgba(0,0,0,0.04)' } }}
              >
                EXIT
              </Button>

              {phase === 'ready' && (
                <Button
                  variant="contained"
                  onClick={() => gridViewRef.current?.triggerLaunch()}
                  disabled={!gridInfo?.articleCount}
                  color="secondary"
                  sx={{ minWidth: 90 }}
                >
                  LAUNCH
                </Button>
              )}
            </Box>
          </Toolbar>

        </AppBar>

        {/* Suggestion Bar — between AppBar and workspace */}
        {phase === 'ready' && availableAdvisors.length > 0 && (
          <SuggestionBar
            activeAdvisors={activeAdvisors}
            onToggleAdvisor={handleToggleAdvisor}
            imageFitStats={gridInfo?.imageFit || { total: 0, pending: 0, accepted: 0, rejected: 0 }}
            spaceFillStats={gridInfo?.spaceFill || { total: 0, pending: 0, accepted: 0, rejected: 0 }}
            onApplyAll={handleApplyAll}
            undoEntries={gridInfo?.undoEntries || []}
            onUndo={handleUndoGlobal}
            availableAdvisors={availableAdvisors}
          />
        )}

        {/* Body */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Polling: ghost skeleton + spinner overlay */}
          {phase === 'polling' && (
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              <GhostSkeleton />
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
                <CircularProgress size={48} />
                <Typography variant="h6" color="text.secondary">
                  Data Integration in progress...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {status || 'Waiting for status...'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Loading data / Rendering GridView: ghost skeleton + spinner overlay */}
          {(phase === 'loading-data' || phase === 'rendering') && (
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              <GhostSkeleton />
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, background: 'rgba(255,255,255,0.6)', zIndex: 2 }}>
                <CircularProgress size={48} />
                <Typography variant="h6" color="text.secondary">
                  {phase === 'loading-data' ? 'Loading Layout Preview data...' : 'Rendering Layout Preview...'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Launching second DI */}
          {phase === 'launching' && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
              <CircularProgress size={48} />
              <Typography variant="h6" color="text.secondary">
                Launching process...
              </Typography>
            </Box>
          )}

          {/* Error */}
          {phase === 'error' && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
              <Alert severity="error" sx={{ maxWidth: 600, width: '100%' }}>
                <AlertTitle>
                  {status === 'validation failed' ? 'Validation Failed' : 'Error'}
                </AlertTitle>
              </Alert>
              {errors.length > 0 && (
                <AttentionBlock attentions={errors} color='error' buttonLabel='Show errors' title='Validation errors' />
              )}
              {warnings.length > 0 && (
                <AttentionBlock attentions={warnings} color='warning' buttonLabel='Show warnings' title='Validation warnings' />
              )}
              <Button variant="outlined" onClick={() => onClose?.()} sx={{ mt: 2 }}>
                Close
              </Button>
            </Box>
          )}

          {/* GridView: montato in fase 'rendering' (nascosto) e 'ready' (visibile) */}
          {(phase === 'rendering' || phase === 'ready') && csvData && (
            <Box sx={{ flex: 1, overflow: 'hidden', visibility: phase === 'ready' ? 'visible' : 'hidden', position: phase === 'ready' ? 'relative' : 'absolute', width: '100%', height: '100%' }}>
              {launchError && (
                <Alert severity="error" onClose={() => setLaunchError(null)} sx={{ mx: 2, mt: 1 }}>
                  Launch failed: {launchError}
                </Alert>
              )}
              <GridView
                ref={gridViewRef}
                onLaunch={handleGridViewLaunch}
                onExit={handleExitClick}
                initialCsvData={csvData}
                layoutConfig={layoutConfig}
                onReady={() => setPhase('ready')}
                onInfo={setGridInfo}
                hasCoverProducts={hasCoverProducts}
              />
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Exit confirmation dialog */}
      <Dialog open={confirmExit} onClose={() => setConfirmExit(false)}>
        <DialogTitle>Exit Layout Preview?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Unsaved changes will be lost and the current request will be cancelled.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExit(false)}>Cancel</Button>
          <Button onClick={handleConfirmExit} color="error">
            Exit
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
